import { useState } from "react";
import { useTheme, mono, Card, Label, Stat } from "./appHelpers";

const THRUSTERS = [
  { id:"conv",    name:"Conventional Cold Gas",  color:"#ef4444", η_in:1.639, η_osc:1.172, η_res:1.000, η_pt:1.000, isp:70,   power:2,   jitter:800, plume:true,  note:"Bang-bang overshoot is structural. Cannot throttle to zero waste." },
  { id:"ep_cg",  name:"EP-ADCS Cold Gas",        color:"#22c55e", η_in:1.000, η_osc:1.000, η_res:1.020, η_pt:1.000, isp:70,   power:0.2, jitter:5,   plume:false, isEP:true, note:"EMMS absorbs all overshoot. Residual η=1.020 from 20ms coil lag only." },
  { id:"ep_wg",  name:"EP-ADCS Warm Gas",        color:"#0e9cc0", η_in:1.000, η_osc:1.000, η_res:1.020, η_pt:1.000, isp:150,  power:8,   jitter:5,   plume:false, isEP:true, note:"Best of both: Isp=150s + η=1.020. Closest to ion for optical payload missions." },
  { id:"ep_mono",name:"EP-ADCS Mono 300s",       color:"#6366f1", η_in:1.000, η_osc:1.000, η_res:1.005, η_pt:1.000, isp:300,  power:12,  jitter:5,   plume:false, isEP:true, note:"Green monopropellant + EMMS. Pre-charge coil during 30s catalyst warm-up → η→1.005." },
  { id:"hall",   name:"Hall Ion Thruster",        color:"#a855f7", η_in:1.000, η_osc:1.000, η_res:1.058, η_pt:1.008, isp:1500, power:150, jitter:800, plume:true,  note:"15min warm-up adds η=1.058. Still needs reaction wheels (jitter remains)." },
];

export default function OvershootPage() {
  const C = useTheme();
  const [sel, setSel] = useState("ep_cg");
  const eta    = t => t.η_in * t.η_osc * t.η_res * t.η_pt;
  const sorted = [...THRUSTERS].sort((a,b) => eta(a)-eta(b));

  return (
    <div>
      <Card color="#0e9cc0">
        <Label>η Ranking — lower is better (η=1.000 = theoretical perfect)</Label>
        {sorted.map((t,i) => {
          const e = eta(t);
          const w = ((e-1)/1.0)*100;
          return (
            <div key={t.id} onClick={() => setSel(t.id)}
              style={{ display:"grid", gridTemplateColumns:"20px 200px 1fr 80px",
                alignItems:"center", gap:10, marginBottom:8, cursor:"pointer",
                padding:"6px 8px", borderRadius:4,
                background: sel===t.id ? `${t.color}10` : "transparent",
                border: sel===t.id ? `1px solid ${t.color}33` : "1px solid transparent" }}>
              <div style={{ ...mono, fontSize:10, color:C.muted }}>#{i+1}</div>
              <div style={{ fontSize:10, color:t.color, fontWeight:600 }}>
                {t.name}
                {t.isEP && <span style={{ fontSize:8, color:t.color, background:`${t.color}22`,
                  padding:"1px 5px", borderRadius:3, marginLeft:6 }}>EP-ADCS</span>}
              </div>
              <div style={{ height:8, background:C.eqBg, borderRadius:4, overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${Math.min(w,100)}%`,
                  background:t.color, borderRadius:4, opacity:0.8 }} />
              </div>
              <div style={{ ...mono, fontSize:12, color:t.color, fontWeight:700, textAlign:"right" }}>
                η={e.toFixed(3)}
              </div>
            </div>
          );
        })}
      </Card>

      {THRUSTERS.filter(t => t.id===sel).map(t => {
        const e = eta(t);
        return (
          <Card key={t.id} color={t.color}>
            <Label color={t.color}>{t.name} — Full Breakdown</Label>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, marginBottom:12 }}>
              {[["η_intrack",t.η_in],["η_oscillatory",t.η_osc],["η_response",t.η_res],["η_pointing",t.η_pt]].map(([l,v]) => (
                <Stat key={l} label={l} value={v.toFixed(3)} unit="" color={v>1.01?"#fbbf24":"#22c55e"} />
              ))}
            </div>
            <div style={{ background:C.eqBg, borderRadius:6, padding:"10px 14px",
              ...mono, fontSize:11, color:t.color, marginBottom:10 }}>
              η_total = {t.η_in.toFixed(3)} × {t.η_osc.toFixed(3)} × {t.η_res.toFixed(3)} × {t.η_pt.toFixed(3)} = {e.toFixed(4)}
              <span style={{ color:C.muted, marginLeft:12 }}>
                (+{((e-1)*100).toFixed(1)}% waste above theoretical minimum)
              </span>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, marginBottom:10 }}>
              <Stat label="Isp"    value={t.isp}                unit="s"    color={C.text} />
              <Stat label="Power"  value={t.power}              unit="W"    color={C.text} />
              <Stat label="Jitter" value={t.jitter}             unit="μrad" color={C.text} />
              <Stat label="Plume"  value={t.plume?"YES":"NO"}   unit=""     color={t.plume?"#ef4444":"#22c55e"} />
            </div>
            <div style={{ ...mono, fontSize:10, color:C.muted, lineHeight:1.7 }}>{t.note}</div>
          </Card>
        );
      })}
    </div>
  );
}
